import { Dispatch, SetStateAction, useState } from "react"
import styled from "styled-components"

import { Text } from "@minuthq/meatball-ui-react"

import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomes } from "src/data/organizations/queries/homeQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { DropdownMultiSelect } from "src/ui/DropdownSelect/DropdownMultiSelect"
import { TSelectOption } from "src/ui/DropdownSelect/DropdownSelect"
import { spacing } from "src/ui/spacing"
type HomeFilterDropdownSelectProps = {
  homeIds: string[]
  setHomeIds: Dispatch<SetStateAction<string[]>>
  onHomeSearch?: (searchValue: string) => void
  onHomeSelected?: (homeId: string, numOfHomes: number) => void
  onHomeDeselected?: (homeId: string) => void
  onClearSelection?: () => void
  onDropdownClick?: () => void
}

export function HomeFilterDropdownSelect({
  homeIds,
  setHomeIds,
  onHomeSearch,
  onHomeSelected,
  onHomeDeselected,
  onClearSelection,
  onDropdownClick,
}: HomeFilterDropdownSelectProps) {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()

  const [homeSearch, setHomeSearch] = useState("")
  const fetchHomes = useFetchHomes({
    orgId,
    filters: { name: homeSearch },
  })
  const homes = fetchHomes.data?.homes || []
  const homeOptions = homes.map((h) => ({
    value: h.home_id,
    label: (
      <LabelWrapper>
        <Text variant="body" as="span">
          {h.name}
        </Text>
        {h.homegroup_name && (
          <>
            •
            <Text variant="bodyS" color="tertiary" as="span">
              {h.homegroup_name}
            </Text>
          </>
        )}
      </LabelWrapper>
    ),
  }))

  function handleHomeSearch(searchValue: string) {
    setHomeSearch(searchValue)
    onHomeSearch?.(searchValue)
  }

  function handleHomeChange({
    checked,
    option,
  }: {
    checked: boolean
    option: TSelectOption<string>
  }) {
    if (checked) {
      // At this point he BE will accept up to about(750 chars) 30 ids
      // in the home_ids parameter so we are limiting the number of
      // homeOptions sent to to the BE to 30
      setHomeIds((prev) =>
        prev.length < 30 ? Array.from(new Set([...prev, option.value])) : prev
      )
      onHomeSelected?.(option.value, homeIds.length + 1)
    } else {
      setHomeIds((prev) => prev.filter((o) => o !== option.value))
      onHomeDeselected?.(option.value)
    }
  }

  function handleClearSelection() {
    setHomeIds([])
    onClearSelection?.()
  }

  function handleClick() {
    onDropdownClick?.()
  }

  return (
    <DropdownMultiSelect
      label={t(langKeys.home, { count: 1 })}
      placeholder={t(langKeys.search)}
      options={homeOptions}
      selectedValues={homeIds}
      onChange={handleHomeChange}
      onSearch={handleHomeSearch}
      onClearSelection={handleClearSelection}
      loading={fetchHomes.isLoading}
      onClick={handleClick}
    />
  )
}

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: start;
  gap: ${spacing.XS2};
  min-width: 0;
  > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`
